import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';

import useDropdown from '../../../../lib/hooks/useDropdown';
import Modal from '../../../../shared/Modal';
import EditVbfBoardForm from '../NavBarActions/Form.jsx';
import { useNotification } from '../../../../shared/Notification/useNotification';
import { editClient } from '../../data';
import defaultAttrs from '../../../../lib/utils/defaultAttrs';

const ClientsRowAction = ({
  filter,
  campaign,
  archiveCampaignAction,
  duplicateCampaignAction,
  unarchiveCampaignAction,
}) => {
  const {
    dropdownRef,
    buttonRef,
    showDropdown,
    setShowDropdown,
  } = useDropdown();

  const { t } = useTranslation(['clients', 'shared']);
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();
  const { userType } = useSelector(state => state.currentUser);

  const { mutate: editMutate } = useMutation(editClient, {
    onSuccess: () => {
      Swal.close();
      queryClient.invalidateQueries(['clients']);
      showSuccess({
        content: t('clients:clientsTable:update'),
        title: t('clients:clientsTable:updateTitle'),
      });
    },
    onError: () => {
      Swal.close();
    },
  });

  const editCampaignAction = () => {
    setShowDropdown(state => !state);

    const editValues = {
      client_name: campaign.clientName,
      demo_name: campaign.name,
      eligible_employees: campaign.eligibleEmployees,
      begins_at:
        campaign.beginsAt !== null ? moment(campaign.beginsAt).toDate() : null,
      ends_at:
        campaign.endsAt !== null ? moment(campaign.endsAt).toDate() : null,
      primary_color: campaign.primaryColor,
      id: campaign.uuid,
    };

    Modal({
      component: (
        <EditVbfBoardForm
          templateList={[]}
          handleCancel={Swal.clickCancel}
          mutate={editMutate}
          isEdit
          editValues={editValues}
        />
      ),
      width: '394px',
      padding: '24px 13px',
    });
  };

  const archiveCampaign = () => {
    setShowDropdown(state => !state);
    archiveCampaignAction();
  };

  const duplicateCampaign = () => {
    setShowDropdown(state => !state);
    duplicateCampaignAction();
  };

  const unarchiveCampaign = () => {
    setShowDropdown(state => !state);
    unarchiveCampaignAction();
  };

  const ActiveActions = () => {
    return (
      <>
        <StyledListItem onClick={editCampaignAction}>
          {t('clientsRowAction.editCampaign')}
        </StyledListItem>
        <StyledListItem onClick={duplicateCampaign}>
          {t('clientsRowAction.duplicate')}
        </StyledListItem>
        {userType !== 'restricted site admin' && (
          <StyledListItem onClick={archiveCampaign} data-cy="archive">
            {t('clientsRowAction.archive')}
          </StyledListItem>
        )}
      </>
    );
  };

  const ArchivedActions = () => {
    return (
      <>
        <StyledListItem onClick={unarchiveCampaign}>
          {t('clientsRowAction.unarchive')}
        </StyledListItem>
      </>
    );
  };

  return (
    <>
      <ButtonHolder
        ref={buttonRef}
        onClick={() => setShowDropdown(state => !state)}
        data-cy="dropDown"
      >
        <EllipsisIcon alt="ellipsis" />
      </ButtonHolder>
      {showDropdown && (
        <Dropdown ref={dropdownRef}>
          {filter === 'archived' ? <ArchivedActions /> : <ActiveActions />}
        </Dropdown>
      )}
    </>
  );
};

export default ClientsRowAction;

const Dropdown = styled.ul`
  margin-top: 38px;
  position: absolute;
  right: 28px;
  border-radius: 8px;
  background: white;
  width: 150px;
  font-weight: 500;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  z-index: 1;
`;

const StyledListItem = styled.li`
  padding: 13px;
  width: 100%;
  color: ${themeGet('colors.primary.100')};
  cursor: pointer;
  &:hover {
    background: ${themeGet('colors.primary.10')};
  }
`;

const ButtonHolder = styled.div`
  font-size: 12px;
  margin-top: 8px;
  margin-bottom: 8px;
  position: relative;
  float: right;
  cursor: pointer;
`;

const EllipsisIcon = styled.i.attrs(
  defaultAttrs({
    className: 'fa-regular fa-ellipsis fa-2x',
  }),
)`
  min-width: 28px;
  max-width: 28px;
  border: 1px solid transparent;
  border-radius: 14px;
  color: ${themeGet('colors.primary.100')};
  &:hover {
    border: 1px solid ${themeGet('colors.black.40')};
    background: ${themeGet('colors.white')};
  }
`;
